import rawEvents from "../data/events.json";
import { loadFonts } from "../fonts";
import {
  formatDay,
  headerYear,
  REQUEST_A_SHOW_URL,
  type TourEvent,
  ticketUrl,
  upcoming,
} from "../tourDates";
import "./TourDates.css";

/**
 * The tour dates, as type in a red outline on the sky.
 *
 * Replaces Bandsintown's widget (tickets 009 and 011) and everything that was
 * built to hide it: the iframe, the rAF height poll, and the 897 ms the widget's
 * own script took to answer. The dates are fetched at build time by
 * `scripts/fetch-events.ts` and committed to `src/data/events.json`, so this
 * component renders from data it already has — no request, no loading frame, no
 * error path, nothing that can arrive late and shove the follow button down the
 * page.
 *
 * That is also why there are no skeleton rows: the design brief asked for the
 * box to be "part of the page, not part of the response", and baked data is the
 * strongest form of that. There is no response to skeleton over.
 *
 * Design: docs/river_live_red_fill_with_scrim_final.html. An earlier cream paper
 * panel (docs/1.html) was built and rejected; the files stay as the record.
 */

const committedEvents = rawEvents as TourEvent[];

/**
 * The one Request a Show link, rendered from both branches (ticket 037).
 *
 * It used to exist in the empty state only, which had it backwards: a fan in a
 * city that is not on the list is exactly who should be asking. One component
 * rather than two copies of the anchor, because two copies drift — and the href
 * is the half of this that has already been wrong once.
 */
function RequestAShow() {
  return (
    <a
      className="tour-dates-request interactive"
      href={REQUEST_A_SHOW_URL}
      target="_blank"
      rel="noopener"
    >
      Request a Show
    </a>
  );
}

/**
 * The right-hand column of a row.
 *
 * Three states, and today every real event is the third: RIVER's five events all
 * come back with `offers: []` and `sold_out: false`, so the CTA column is empty
 * on every row the site can currently draw. The row has to look composed without
 * it, which is why the venue block takes the freed width rather than the column
 * being reserved.
 */
function RowCta({ event }: { event: TourEvent }) {
  if (event.soldOut) {
    return (
      <span className="tour-date-tickets tour-date-soldout" aria-disabled="true">
        Sold Out
      </span>
    );
  }
  const tickets = ticketUrl(event);
  if (!tickets) return null;
  return (
    <a className="tour-date-tickets interactive" href={tickets} target="_blank" rel="noopener">
      Tickets
    </a>
  );
}

function Row({ event }: { event: TourEvent }) {
  const city = [event.venue.city, event.venue.country].filter(Boolean).join(", ");
  return (
    <li className="tour-date">
      <span className="tour-date-day">{formatDay(event.datetime)}</span>
      <span className="tour-date-where">
        {/*
          The venue name is the row's link when nothing else in the row is one.
          Not in the reference, which assumed every row would carry a Tickets
          link — with `offers` empty on every real event, taking the reference
          literally would ship a list where no row does anything at all. It
          inherits the row's type exactly and only underlines on hover, so it
          reads as the reference until it is pointed at.
        */}
        {event.url ? (
          <a
            className="tour-date-venue interactive"
            href={event.url}
            target="_blank"
            rel="noopener"
          >
            {event.venue.name}
          </a>
        ) : (
          <span className="tour-date-venue">{event.venue.name}</span>
        )}
        <span className="tour-date-city">{city}</span>
      </span>
      <RowCta event={event} />
    </li>
  );
}

/**
 * `events` defaults to the committed data, which is what the site always passes.
 * The prop exists so the tests can render both branches: every real event is in
 * the past today, so without it the populated layout could only be checked by
 * editing `events.json`.
 */
export function TourDates({ events = committedEvents }: { events?: TourEvent[] } = {}) {
  // Registered here rather than at app boot: this is the only component that
  // sets Instrument Serif, and /live is mounted during the landing page's idle
  // period anyway (App.tsx), so the faces are ready well before anyone asks for
  // the page — without the other three pages paying for a typeface they do not
  // use.
  loadFonts();

  const dates = upcoming(events);

  /*
    The empty state is a different box, not the same box with different contents:
    no header, no rule, no year, no line of copy, and sized to what is in it. A
    "Upcoming Dates" heading over a box that says there are none is a label
    arguing with its own contents, and a line announcing the emptiness says less
    than the button does. What is left is the one thing there is to do.
  */
  if (!dates.length) {
    return (
      <section className="tour-dates tour-dates-is-empty">
        <RequestAShow />
      </section>
    );
  }

  const year = headerYear(dates);

  return (
    <section className="tour-dates">
      <header className="tour-dates-head">
        <h2 className="tour-dates-title">Upcoming Dates</h2>
        {year ? <span className="tour-dates-year">{year}</span> : null}
      </header>

      <ul className="tour-dates-list">
        {dates.map((event) => (
          <Row event={event} key={event.id} />
        ))}
      </ul>

      {/*
        Its own row rather than loose in the section: the button needs the join
        under the list drawn for it, and the empty state's spacing belongs to the
        empty state's box. The two branches share the link, not its placement.
      */}
      <div className="tour-dates-request-row">
        <RequestAShow />
      </div>
    </section>
  );
}
